import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useGiftCodes from "@babackapp/private-api-client/client/use-gift-codes";
import useTranslation from "@babackapp/translations/use-translation";
import { DateRangePicker } from "@babackapp/ui/components/date-range-picker";
import { Input } from "@babackapp/ui/components/input";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  OnChangeFn,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import { subMonths } from "date-fns";
import { useCallback, useState } from "react";
import { GiftCodesDataTable } from "./components/gift-codes-table";
import { GiftCodesStatsHeader } from "./components/gift-codes-stats-header";
import { columns } from "./components/gift-codes-table-columns";

const SORTABLE_COLUMNS = ["createdAt", "usedAt"] as const;
type SortableColumn = (typeof SORTABLE_COLUMNS)[number];

export default function GiftCodes() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({
    from: subMonths(new Date(), 1).getTime(),
    to: Date.now(),
  });

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);

  const activeSortColumn = sorting[0];
  const rawId = activeSortColumn?.id;
  const sortBy =
    rawId && (SORTABLE_COLUMNS as readonly string[]).includes(rawId)
      ? (rawId as SortableColumn)
      : undefined;
  const sortOrder = activeSortColumn
    ? activeSortColumn.desc
      ? "desc"
      : "asc"
    : undefined;

  const { data: giftCodesResult } = useGiftCodes({
    shopId: shop.id,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    search,
    sortBy,
    sortOrder,
    fromTimestamp: dateRange.from,
    toTimestamp: dateRange.to,
  });

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      setPagination(updater);
    },
    [],
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    setSorting(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const totalCount = giftCodesResult?.totalCount ?? 0;
  const currentPageData = giftCodesResult?.data ?? [];

  return (
    <div className="w-full flex justify-center pt-16 px-4">
      <div className="w-full flex-1 max-w-[1300px] flex flex-col gap-8">
        <div className={"flex flex-col flex-grow flex-1 gap-8"}>
          <div className="flex justify-between items-center gap-2 w-full">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("pdfGift.giftCode")}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground max-w-[500px]">
                {t("returns.subtitle")}
              </p>
            </div>
          </div>

          <GiftCodesStatsHeader
            fromTimestamp={dateRange.from}
            toTimestamp={dateRange.to}
          />

          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute h-full w-8 flex items-center justify-center pointer-events-none">
                  <MagnifyingGlassIcon className="z-50 size-4 text-muted-foreground" />
                </div>
                <Input
                  placeholder={t("returns.search")}
                  value={search}
                  className="z-0 max-w-[300px] pl-8"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  }}
                />
              </div>
              <div className="flex-shrink-0">
                <DateRangePicker
                  range={{
                    from: dateRange.from,
                    to: dateRange.to,
                  }}
                  onSelect={(range) => {
                    if (range.from && range.to) {
                      setDateRange({
                        from: range.from,
                        to: range.to,
                      });
                      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }
                  }}
                />
              </div>
            </div>
            <div className="overflow-hidden border rounded-md">
              <GiftCodesDataTable
                columns={columns}
                data={currentPageData}
                rowCount={totalCount}
                pageSize={pagination.pageSize}
                pageIndex={pagination.pageIndex}
                sorting={sorting}
                onPaginationChange={onPaginationChange}
                onSortingChange={onSortingChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
